import {
  Text,
  IconCircleInfo,
  GreyGrey40,
  GreyGrey50,
  GreyGrey60,
  GreyGrey90,
  IndigoIndigo50,
  Link,
  Primary13SemiBold,
  Primary13Regular,
  Primary14SemiBold,
  Primary20SemiBold,
  InfoPopover,
  XYChart,
  XYChartTooltipTemplate,
  Box,
  Flex
} from '@sequencehq/core-components'
import { FilterSelectInput } from 'modules/Insights/common/FilterSelectInput/FilterSelectInput'
import { WidgetLoading } from 'modules/Insights/common/WidgetLoading/WidgetLoading'
import { useUsageMetricsWidget } from 'modules/Insights/widgets/UsageMetricsWidget/useUsageMetricsWidget'
import { format, formatDateRange } from '@sequencehq/utils/dates'

interface UsageMetricsWidgetProps {
  dateRange: {
    start: Date
    end: Date
  }
}

export const UsageMetricsWidget = ({ dateRange }: UsageMetricsWidgetProps) => {
  const {
    isLoading,
    usageMetricTypeField,
    chart: { data, total, unit, unitTickValues, tooltipFormat }
  } = useUsageMetricsWidget(dateRange)

  if (isLoading) {
    return <WidgetLoading />
  }

  return (
    <Box
      width="390px"
      height="291px"
      boxShadow="0 2px 4px 0 rgba(15, 23, 42, 0.08)"
      border={`1px solid ${GreyGrey40}`}
      borderRadius="lg"
      p={4}
    >
      <Flex gap={2}>
        <Flex flexGrow="1" flexDirection="column" gap={2}>
          <Text as={Flex} {...Primary13SemiBold} color={GreyGrey60} gap={1}>
            Usage Metrics
            <InfoTooltip />
          </Text>
          <Text {...Primary20SemiBold} color={GreyGrey90}>
            {total.toLocaleString('en-US')}
          </Text>
          <Text {...Primary13Regular} color={GreyGrey60}>
            {formatDateRange({
              from: new Date(dateRange.start),
              to: new Date(dateRange.end)
            })}
          </Text>
        </Flex>
        <Flex width="120px" alignSelf="baseline">
          {usageMetricTypeField?.options?.length > 0 && (
            <FilterSelectInput
              placeholder="Select metric"
              value={usageMetricTypeField.value}
              options={usageMetricTypeField.options}
              onChange={usageMetricTypeField.onChange}
            />
          )}
        </Flex>
      </Flex>
      <Box mt={4} height="170px" width="100%">
        <XYChart
          variant="line"
          dateFrom={format(dateRange.start, 'yyyy-MM-dd')}
          dateTo={format(dateRange.end, 'yyyy-MM-dd')}
          data={data}
          unitTickValues={unitTickValues}
          unit={unit}
          renderTooltip={({ date, value }) => (
            <XYChartTooltipTemplate>
              <Text {...Primary13SemiBold} color="gray.70">
                {format(new Date(date), 'EEE MMM d, yyyy')}
              </Text>
              <>
                <Text
                  as={Flex}
                  {...Primary13Regular}
                  color="gray.70"
                  alignItems="center"
                  gap={2}
                >
                  <Flex h={2} w={2} bg={IndigoIndigo50} borderRadius="full" />
                  {format(new Date(date), 'EEE MMM d, yyyy')}
                </Text>
                <Text {...Primary13SemiBold} color="gray.80">
                  {tooltipFormat(value)}
                </Text>
              </>
            </XYChartTooltipTemplate>
          )}
        />
      </Box>
    </Box>
  )
}

const InfoTooltip = () => {
  return (
    <InfoPopover
      variant="info"
      minimal
      padding="0"
      placement="top"
      body={
        <Box width={300}>
          Usage metrics aggregate units of usage into overall measures of how
          your users interact with your product.{' '}
          <Link
            to="https://docs.sequencehq.com/metrics-events/usage-metrics"
            isExternal
            {...Primary14SemiBold}
            color="indigo.50"
          >
            Learn more
          </Link>
        </Box>
      }
      icon={<IconCircleInfo color={GreyGrey50} />}
    />
  )
}
